import { useState, useEffect } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Button from '@cloudscape-design/components/button'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Flashbar from '@cloudscape-design/components/flashbar'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import type { Provider } from '@/lib/types'
import { useUpdateProvider } from '@/hooks/useProviders'
import type { UpdateProviderInput } from '@/hooks/useProviders'

interface EditProviderModalProps {
  provider: Provider | null
  clientId: string
  onClose: () => void
}

export default function EditProviderModal({ provider, clientId, onClose }: EditProviderModalProps) {
  const { updateProvider, loading } = useUpdateProvider()
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([])

  const [providerName, setProviderName] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [practiceName, setPracticeName] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [fax, setFax] = useState('')
  const [email, setEmail] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [ehrSystem, setEhrSystem] = useState('')
  const [entityType, setEntityType] = useState('')

  useEffect(() => {
    if (provider) {
      setProviderName(provider.provider_name ?? '')
      setSpecialty(provider.specialty ?? '')
      setPracticeName(provider.practice_name ?? '')
      setCity(provider.city ?? '')
      setState(provider.state ?? '')
      setZip(provider.zip ?? '')
      setAddress(provider.address ?? '')
      setPhone(provider.phone ?? '')
      setFax(provider.fax ?? '')
      setEmail(provider.email ?? '')
      setWebsiteUrl(provider.website_url ?? '')
      setEhrSystem(provider.ehr_system ?? '')
      setEntityType(provider.entity_type ?? '')
      setFlash([])
    }
  }, [provider])

  if (!provider) return null

  async function handleSubmit() {
    if (!provider) return
    setFlash([])
    const input: UpdateProviderInput = {
      provider_name: providerName || undefined,
      specialty: specialty || undefined,
      practice_name: practiceName || undefined,
      city: city || undefined,
      state: state || undefined,
      zip: zip || undefined,
      address: address || undefined,
      phone: phone || undefined,
      fax: fax || undefined,
      email: email || undefined,
      website_url: websiteUrl || undefined,
      ehr_system: ehrSystem || undefined,
      entity_type: entityType || undefined,
    }
    try {
      await updateProvider(clientId, provider._id, input)
      onClose()
    } catch (err) {
      setFlash([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to update provider. Please try again.',
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
    }
  }

  return (
    <Modal
      visible={!!provider}
      onDismiss={onClose}
      header={`Edit Provider — ${provider.provider_name}`}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={loading} onClick={handleSubmit}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {flash.length > 0 && <Flashbar items={flash} />}
        <Form>
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label="Name">
                <Input
                  value={providerName}
                  onChange={({ detail }) => setProviderName(detail.value)}
                  placeholder="Provider name"
                />
              </FormField>
              <FormField label="Specialty">
                <Input
                  value={specialty}
                  onChange={({ detail }) => setSpecialty(detail.value)}
                  placeholder="Specialty"
                />
              </FormField>
            </ColumnLayout>
            <FormField label="Practice Name">
              <Input
                value={practiceName}
                onChange={({ detail }) => setPracticeName(detail.value)}
                placeholder="Practice name"
              />
            </FormField>
            <ColumnLayout columns={3}>
              <FormField label="City">
                <Input
                  value={city}
                  onChange={({ detail }) => setCity(detail.value)}
                  placeholder="City"
                />
              </FormField>
              <FormField label="State">
                <Input
                  value={state}
                  onChange={({ detail }) => setState(detail.value)}
                  placeholder="State"
                />
              </FormField>
              <FormField label="ZIP">
                <Input
                  value={zip}
                  onChange={({ detail }) => setZip(detail.value)}
                  placeholder="ZIP code"
                />
              </FormField>
            </ColumnLayout>
            <FormField label="Address">
              <Input
                value={address}
                onChange={({ detail }) => setAddress(detail.value)}
                placeholder="Street address"
              />
            </FormField>
            <ColumnLayout columns={2}>
              <FormField label="Phone">
                <Input
                  value={phone}
                  onChange={({ detail }) => setPhone(detail.value)}
                  placeholder="Phone number"
                />
              </FormField>
              <FormField label="Fax">
                <Input
                  value={fax}
                  onChange={({ detail }) => setFax(detail.value)}
                  placeholder="Fax number"
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={2}>
              <FormField label="Email">
                <Input
                  value={email}
                  onChange={({ detail }) => setEmail(detail.value)}
                  placeholder="Email address"
                  type="email"
                />
              </FormField>
              <FormField label="Website URL">
                <Input
                  value={websiteUrl}
                  onChange={({ detail }) => setWebsiteUrl(detail.value)}
                  placeholder="https://example.com"
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={2}>
              <FormField label="EHR System">
                <Input
                  value={ehrSystem}
                  onChange={({ detail }) => setEhrSystem(detail.value)}
                  placeholder="EHR system name"
                />
              </FormField>
              <FormField label="Entity Type">
                <Input
                  value={entityType}
                  onChange={({ detail }) => setEntityType(detail.value)}
                  placeholder="Entity type"
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Form>
      </SpaceBetween>
    </Modal>
  )
}
